// @react
import React from 'react'
// @graphql
import {
  GetUserNetworkActivityQuery,
  IAbstractSearchItem,
  NetworkActivityItem,
} from 'lib/graphql/graphqlTypes'
// @libraries
import { Typography } from '@material-ui/core'
// @common
import Helper, { ActivityAction } from 'utils/Helper.class'
import strings from 'lib/constants/strings'
// @components
import Box from '@bit/atd.web.box'
import TextLink from '@bit/atd.web.text-link'
import Grid from '@bit/atd.web.grid'
import Avatar from '@bit/atd.web.avatar'
import Stats from '@bit/atd.web.stats'
import { StatProps } from '@bit/atd.web.stat'
import Spacer from '@bit/atd.web.spacer'
import { ReactElement } from 'react'
import { useStyles } from './style'
import * as uuid from 'uuid'

type PropTypes = {
  activityItems: GetUserNetworkActivityQuery['activity']['data'] | undefined
  followedCount: number | undefined
  followingCount: number | undefined
  groupsCount: number | undefined
}

/**
 *
 * @param props
 * @constructor
 */
const Network = (props: PropTypes): ReactElement => {
  /******************************************
   * PROPS
   ******************************************/

  const { activityItems, followedCount, followingCount, groupsCount } = props

  /******************************************
   * HOOKS
   ******************************************/
  const classes = useStyles()

  const statsProps: Partial<StatProps> = {
    labelColor: '#424140',
    labelSize: 10,
    labelWeight: 500,
    countColor: '#E13000',
    countSize: 22,
    countWeight: 600,
    alignment: 'label-bottom',
    padding: '0px 10px',
  }

  /**
   *
   */
  const groupNetwork = () => {
    if (!activityItems) return
    return groupBy(
      activityItems.map((data) => {
        return {
          ...data,
          publishDate: Helper.miniDateFormatter(data.content?.publishDate),
        }
      }),
      'publishDate',
    )
  }

  /**
   *
   * @param items
   * @param key
   */
  const groupBy = (items: any[], key: string) =>
    items.reduce(
      (result, item) => ({
        ...result,
        [item[key]]: [...(result[item[key]] || []), item],
      }),
      {},
    )

  /**
   *
   * @param action
   * @param content
   */
  const actionLabelFormatter = (
    action: ActivityAction,
    content: IAbstractSearchItem,
  ): string => {
    const article = `${Helper.articleChooser(content?.contentTypes?.lvl0)}`
    const contentType = content?.contentTypes?.lvl0 ?? ''
    if (contentType !== 'People') {
      return `${Helper.actionTextHelper(action)} ${article} ${contentType}`
    }
    return `${Helper.actionTextHelper(action)}`
  }

  /**
   *
   */
  const renderActivity = () => {
    const grouped = groupNetwork()
    const result = []
    for (const [, value] of Object.entries(grouped)) {
      result.push(value)
    }

    return result.map((item: any) =>
      renderGroupedDate(item, item[0].publishDate),
    )
  }

  /**
   *
   * @param item
   * @param index
   * @param amount
   */
  const renderActivityBox = (
    item: NetworkActivityItem,
    index: number,
    amount: number,
  ) => {
    // @todo not great, but it's difficult to find a unique key for accidentally duplicate network feed items
    const setID = uuid.v4()
    return (
      <React.Fragment key={`${item.id}_${setID}`}>
        {index !== 0 && <Spacer size={3} />}

        <Grid className={classes.activityItem} container>
          <Grid className={classes.avatarWrapper} item>
            <Avatar
              size={32}
              src={item?.user?.imageUrl || ''}
              alt={`${item.user.firstName} ${item.user.lastName}`}
              bgColor={Helper.randomColorChooser()}
            >
              {Helper.getAcronymFromSentence(
                `${item.user.firstName} ${item.user.lastName}`,
              )}
            </Avatar>
          </Grid>

          <Grid className={classes.textWrapper} item xs>
            <Grid container>
              <Grid item xs={12}>
                <TextLink
                  className={classes.textName}
                  size={'small'}
                  variant="secondary"
                  href={item.user.url}
                >
                  {`${item.user.firstName} ${item.user.lastName}`}
                </TextLink>
              </Grid>
              <Grid item xs={12}>
                <span className={classes.textXXSmall}>
                  {actionLabelFormatter(
                    item.action as ActivityAction,
                    item.content,
                  )}
                </span>
              </Grid>
              <Grid item xs={12}>
                <TextLink
                  className={classes.textContext}
                  size={'medium'}
                  href={item.content.url}
                >
                  {item.content.title}
                </TextLink>
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        {index + 1 !== amount && <Spacer size={3} />}
      </React.Fragment>
    )
  }

  /**
   *
   * @param items
   * @param date
   */
  const renderGroupedDate = (items: any[], date: string) => {
    return (
      <React.Fragment key={date}>
        <Spacer size={12} />

        <div className={classes.boxHeader}>
          <Typography component={'p'}>{date}</Typography>
        </div>

        {items.map((item: any, i) => renderActivityBox(item, i, items.length))}
      </React.Fragment>
    )
  }

  const renderStats = () => {
    const stats = [
      {
        ...statsProps,
        label: 'Following',
        count: followingCount,
      },
      {
        ...statsProps,
        label: 'Followers',
        count: followedCount,
      },
    ]

    if (groupsCount !== undefined && groupsCount > 0) {
      stats.push({
        ...statsProps,
        label: 'Groups',
        count: groupsCount,
      })
    }

    return stats
  }

  return (
    <Box
      variant="subModule"
      header={{
        title: 'Network Activity',
      }}
      footer={{
        link: {
          url: Helper.getPathFromRouteKey(
            'MYATD_ACTIVITYHISTORY_NETWORKACTIVITY',
          ),
          label: strings.DASHBOARD.VIEW_ALL_NETWORK_ACTIVITY,
        },
      }}
    >
      <>
        <div className={classes.boxHeader}>
          <Typography component={'p'}>Following</Typography>
        </div>

        <Stats
          border={'1px solid #DBD9D6'}
          divider={{
            width: 1,
            color: '#DBD9D6',
            height: 40,
          }}
          stats={renderStats()}
        />

        {renderActivity()}
      </>
    </Box>
  )
}

export default Network
